import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import ReceiptPrintModal from "@/pages/dashboard/payments/components/ReceiptPrintModal";
import { useAuth } from "@/contexts/AuthContext";
import { getSchoolInfo } from "@/services/schoolService";
import type { SchoolInfo } from "@/types";
import type { ReceiptData } from "@/utils/receipt";
import { goOnline, listOutbox, removeFromOutbox, retryOutbox, syncOutbox, type OutboxItem } from "@/offline/outbox";
import { prepareEverything, type Tracker } from "@/offline/prefetch";
import { waitForNetworkIdle } from "@/services/http";
import { navItems } from "./navItems";
import { OFFLINE_MAX_DAYS } from "@/offline/session";
import { setForcedOffline, useOfflineStatus } from "@/offline/status";
import { plural } from "@/utils/format";

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Parcourt toutes les pages permises, puis chacun de leurs onglets, en attendant la fin de leurs chargements :
 * chaque page garde exactement ce qu'elle affiche. Revient ensuite à la page de départ.
 */
async function visitAllPages(tracker: Tracker, navigate: (to: string) => void, can: (p: string) => boolean, back: string) {
  const pages = navItems.filter(
    (item) => !item.platform && (item.permission ? can(item.permission) : item.anyOf ? item.anyOf.some(can) : true),
  );
  tracker.count(pages.length + 1, 0);
  for (const page of [...pages, { to: "/dashboard/account", label: "Mon compte" }]) {
    tracker.step(`Page « ${page.label} »`);
    navigate(page.to);
    await pause(400);
    await waitForNetworkIdle();
    const tabs = [...(document.querySelector("main")?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])];
    for (const tab of tabs) {
      if (tab.getAttribute("aria-selected") === "true" || !tab.isConnected) continue;
      tab.click();
      await pause(300);
      await waitForNetworkIdle();
    }
    tracker.count(0, 1);
  }
  navigate(back);
  await pause(300);
  await waitForNetworkIdle();
}

const dateTime = (ms: number) =>
  new Date(ms).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/**
 * « Passer en mode hors ligne » : garde sur l'appareil les données de toutes les pages (avec l'avancement),
 * puis coupe les appels au serveur. Tout se lit sur l'appareil ; notes, paiements et inscriptions attendent
 * « Repasser en ligne ».
 */
export function GoOfflineDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { preparing } = useOfflineStatus();
  const [result, setResult] = useState<{ total: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
    }
  }, [open]);

  const start = async () => {
    setError(null);
    try {
      const outcome = await prepareEverything(can, (tracker) => visitAllPages(tracker, navigate, can, pathname));
      // Rien n'a pu être lu : le serveur ne répond pas, on ne coupe pas sur des données absentes.
      if (outcome.total > 0 && outcome.failed === outcome.total) {
        setError("Le serveur ne répond pas : impossible de préparer les données. Réessayez avec la connexion.");
        return;
      }
      setForcedOffline(true);
      setResult(outcome);
    } catch {
      setError("Préparation impossible. Réessayez avec la connexion.");
    }
  };

  const percent = preparing && preparing.total ? Math.round((preparing.done / preparing.total) * 100) : 0;

  return (
    <Modal
      open={open}
      onClose={() => !preparing && onClose()}
      title={result ? "Mode hors ligne activé" : "Passer en mode hors ligne"}
      subtitle={result ? "L'application ne contacte plus le serveur" : "Toutes les données sont d'abord gardées sur cet appareil"}
      icon={result ? "ri-wifi-off-line" : "ri-download-cloud-2-line"}
      size="sm"
      footer={
        result ? (
          <Button onClick={onClose}>Continuer hors ligne</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose} disabled={!!preparing}>
              Annuler
            </Button>
            <Button icon="ri-download-cloud-2-line" onClick={() => void start()} disabled={!!preparing}>
              {preparing ? "Préparation..." : "Préparer et passer hors ligne"}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm leading-relaxed text-foreground-700">
          <p>
            {plural(result.total - result.failed, "lecture gardée", "lectures gardées")} sur cet appareil. Toutes les pages
            s'affichent avec ces données, même sans réseau.
          </p>
          {result.failed > 0 && (
            <p className="rounded-md bg-warning-50 px-3 py-2 text-warning-800">
              {plural(result.failed, "lecture n'a", "lectures n'ont")} pas pu être faite{result.failed > 1 ? "s" : ""} : la page concernée
              pourra afficher « données pas encore gardées ».
            </p>
          )}
          <p>
            Les <strong>notes</strong>, <strong>paiements</strong> et <strong>inscriptions</strong> saisis sont gardés et envoyés quand
            vous cliquerez sur <strong>« Repasser en ligne »</strong> (en haut de l'écran ou dans le menu du compte).
          </p>
        </div>
      ) : (
        <div className="space-y-4 text-sm leading-relaxed text-foreground-700">
          <p>
            Utile avant une coupure annoncée ou avec une connexion lente ou chère : l'application lit tout sur l'appareil et ne
            consomme plus de données. Les notes, paiements et inscriptions saisis partent quand vous repassez en ligne.
          </p>
          <p className="text-xs text-foreground-500">
            L'application ouvre une à une toutes les pages et leurs onglets, puis le dossier de chaque élève et les bulletins :
            comptez quelques minutes selon la connexion. Sans retour en ligne pendant {OFFLINE_MAX_DAYS} jours, il faudra se
            reconnecter.
          </p>
          {preparing && (
            <div>
              <div className="mb-1.5 flex justify-between text-xs text-foreground-600">
                <span className="truncate">{preparing.label ?? "Données gardées"}</span>
                <span className="shrink-0 tabular-nums">{percent} %</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-background-200">
                <div className="h-full rounded-full bg-primary-600 transition-[width] duration-300" style={{ width: `${percent}%` }}></div>
              </div>
            </div>
          )}
          {error && <p className="rounded-md bg-accent-50 px-3 py-2 text-accent-800">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/**
 * État du hors-ligne dans l'en-tête : coupure (avec la date des données affichées), actions en attente
 * d'envoi, actions à vérifier. Un clic ouvre la liste des actions gardées sur l'appareil.
 */
export default function OfflineIndicator() {
  const { online, cachedAt, pending, review, syncing, forced } = useOfflineStatus();
  const [open, setOpen] = useState(false);

  if (online && pending === 0 && review === 0) return null;

  const tone = review > 0 ? "border-accent-200 bg-accent-50 text-accent-800" : !online ? "border-warning-200 bg-warning-50 text-warning-800" : "border-primary-200 bg-primary-50 text-primary-800";
  const icon = syncing ? "ri-loader-4-line animate-spin" : !online ? "ri-wifi-off-line" : review > 0 ? "ri-error-warning-line" : "ri-upload-cloud-2-line";
  const text = review > 0 ? `${review} à vérifier` : forced ? "Mode hors ligne" : !online ? "Hors ligne" : `${pending} en attente`;
  // Écran étroit : l'icône et le nombre seulement (l'en-tête n'a pas la place pour le libellé).
  const count = review > 0 ? review : pending > 0 ? pending : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`press inline-flex h-9 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium cursor-pointer xl:px-3 ${tone}`}
        aria-label={text}
        title={text}
      >
        <i className={`${icon} text-sm`} aria-hidden="true"></i>
        {count !== null && <span className="tabular-nums xl:hidden">{count}</span>}
        <span className="hidden whitespace-nowrap xl:inline">{text}</span>
        {!online && pending > 0 && <span className="hidden whitespace-nowrap xl:inline">· {pending} en attente</span>}
        {!online && cachedAt && <span className="hidden whitespace-nowrap 2xl:inline">· données du {dateTime(cachedAt)}</span>}
      </button>
      <SyncPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function SyncPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { online, cachedAt, pending, review, syncing, forced } = useOfflineStatus();
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [removing, setRemoving] = useState<OutboxItem | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [printing, setPrinting] = useState<{ data: ReceiptData; school: SchoolInfo } | null>(null);

  const load = useCallback(() => void listOutbox().then(setItems), []);
  const printProvisional = async (data: ReceiptData) => setPrinting({ data, school: await getSchoolInfo() });
  useEffect(() => {
    if (open) load();
  }, [open, load, pending, review, syncing]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={forced ? "Mode hors ligne activé" : online ? "Actions gardées sur l'appareil" : "Vous êtes hors ligne"}
      subtitle={online ? "Envoyées automatiquement au serveur" : cachedAt ? `Données affichées : celles du ${dateTime(cachedAt)}` : undefined}
      icon={online ? "ri-upload-cloud-2-line" : "ri-wifi-off-line"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          {forced ? (
            <Button
              icon={leaving ? "ri-loader-4-line animate-spin" : "ri-wifi-line"}
              disabled={leaving}
              onClick={() => {
                setLeaving(true);
                void goOnline();
              }}
            >
              {leaving ? "Envoi..." : pending > 0 ? `Repasser en ligne et envoyer (${pending})` : "Repasser en ligne"}
            </Button>
          ) : (
            <Button icon="ri-refresh-line" disabled={syncing || pending === 0} onClick={() => void syncOutbox().then(load)}>
              {syncing ? "Envoi..." : "Envoyer maintenant"}
            </Button>
          )}
        </>
      }
    >
      {forced && (
        <p className="mb-4 rounded-md bg-primary-50 px-3 py-2.5 text-sm leading-relaxed text-primary-900">
          Vous avez choisi de travailler hors ligne : l'application ne contacte plus le serveur. Repassez en ligne pour envoyer
          vos saisies et retrouver les données à jour.
        </p>
      )}
      {!online && !forced && (
        <p className="mb-4 rounded-md bg-warning-50 px-3 py-2.5 text-sm leading-relaxed text-warning-800">
          Vous consultez les données gardées sur cet appareil. Les <strong>notes</strong>, <strong>paiements</strong> et{" "}
          <strong>inscriptions</strong> saisis sont gardés et envoyés au retour de la connexion ; les autres actions demandent
          la connexion. Sans connexion pendant {OFFLINE_MAX_DAYS} jours, il faudra se reconnecter.
        </p>
      )}
      {items.length === 0 ? (
        <p className="rounded-md border border-dashed border-background-300 py-8 text-center text-sm text-foreground-500">
          Aucune action en attente : tout est envoyé.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="rounded-md border border-background-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900">{item.label}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">Saisie le {dateTime(item.createdAt)}</p>
                  {item.provisional && (
                    <Button
                      size="sm"
                      variant="outline"
                      icon="ri-receipt-line"
                      className="mt-2"
                      onClick={() => void printProvisional(item.provisional as ReceiptData)}
                    >
                      Reçu provisoire
                    </Button>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                    item.status === "review" ? "bg-accent-50 text-accent-700" : "bg-primary-50 text-primary-700"
                  }`}
                >
                  {item.status === "review" ? "À vérifier" : "En attente"}
                </span>
              </div>
              {item.status === "review" && (
                <>
                  <p className="mt-2 text-xs leading-relaxed text-accent-800">{item.message}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.kind === "enrollment" && item.message?.includes("existe déjà") ? (
                      <Button size="sm" icon="ri-user-add-line" onClick={() => void retryOutbox(item.id, true).then(load)}>
                        Créer quand même
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" icon="ri-refresh-line" onClick={() => void retryOutbox(item.id).then(load)}>
                        Réessayer
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" icon="ri-delete-bin-6-line" onClick={() => setRemoving(item)}>
                      Supprimer
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={removing !== null}
        title="Supprimer cette action ?"
        message={`« ${removing?.label ?? ""} » ne sera jamais envoyée au serveur.`}
        confirmLabel="Supprimer"
        onConfirm={() => removing && void removeFromOutbox(removing.id).then(load)}
        onClose={() => setRemoving(null)}
      />
      {printing && (
        <ReceiptPrintModal open onClose={() => setPrinting(null)} data={printing.data} school={printing.school} />
      )}
    </Modal>
  );
}
